import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Checkbox, Button } from '@common/musicHubCommon';
import { ROUTES } from '../../../routes';
import { reportPageView } from '../../../services/reportLog';
import './index.scss';

interface AdvancedFormData {
  recordLabel: string;
  publisher: string;
  isrc: string;
  explicitContent: boolean;
  pLine: string;
}

const ReleaseAdvanced: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  useEffect(() => {
    reportPageView('content_0014', { withArtistId: true });
  }, []);

  // 检查是否有基本信息
  useEffect(() => {
    const basicInfo = sessionStorage.getItem('releaseBasicInfo');
    if (!basicInfo) {
      navigate(ROUTES.releaseBasicInfo);
    }
  }, [navigate]);

  // 表单数据（所有字段都是可选的）
  const [formData, setFormData] = useState<AdvancedFormData>({
    recordLabel: '',
    publisher: '',
    isrc: '',
    explicitContent: false,
    pLine: '',
  });

  // 处理返回
  const handleBack = () => {
    navigate(ROUTES.releaseBasicInfo);
  };

  // 处理输入变化
  const handleInputChange = (field: keyof AdvancedFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // 处理下一步（所有字段可选，可以直接进入下一步）
  const handleNext = () => {
    // 保存高级信息到 sessionStorage
    sessionStorage.setItem('releaseAdvancedInfo', JSON.stringify(formData));
    navigate(ROUTES.releaseSchedule);
  };

  return (
    <div className="release-advanced">
      {/* 页面标题 */}
      <div className="release-advanced__header">
        <button className="back-btn" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
          <span>Release Song</span>
        </button>
      </div>

      {/* 主内容 */}
      <div className="release-advanced__content">
        <div className="release-advanced__card">
          {/* 标题 */}
          <h2 className="release-advanced__title">Advanced details</h2>

          {/* 表单 */}
          <Form form={form} layout="vertical" className="release-advanced__form">
            {/* 唱片公司 */}
            <Form.Item 
              label="Record Label"
              className="form-item"
            >
              <Input
                value={formData.recordLabel}
                onChange={(e) => handleInputChange('recordLabel', e.target.value)}
                placeholder="If your track is released under a specific record label, you can add here."
              />
            </Form.Item>

            {/* 出版商 */}
            <Form.Item 
              label="Publisher"
              className="form-item"
            >
              <Input
                value={formData.publisher}
                onChange={(e) => handleInputChange('publisher', e.target.value)}
                placeholder="Enter publisher name"
              />
            </Form.Item>

            {/* ISRC */}
            <Form.Item 
              label="ISRC"
              className="form-item"
            >
              <Input
                value={formData.isrc}
                onChange={(e) => handleInputChange('isrc', e.target.value)}
                placeholder="International Standard Recording Code"
              />
            </Form.Item>

            {/* 显式内容 */}
            <Form.Item className="form-item form-item--checkbox">
              <div className="explicit-section">
                <label className="explicit-section__label">Contains explicit content</label>
                <p className="explicit-section__hint">
                  Please check this if your track contains explicit content. The badge will be display next to your track title.
                </p>
                <div className="explicit-section__checkbox">
                  <Checkbox
                    checked={formData.explicitContent}
                    onChange={(e) => handleInputChange('explicitContent', e.target.checked)}
                  >
                    Explicit content
                  </Checkbox>
                  <span className="explicit-badge">E</span>
                </div>
              </div>
            </Form.Item>

            {/* P Line */}
            <Form.Item 
              label="P Line"
              className="form-item"
            >
              <Input
                value={formData.pLine}
                onChange={(e) => handleInputChange('pLine', e.target.value)}
                placeholder="℗ 2026 Your Name"
              />
            </Form.Item>

            {/* 提交按钮 */}
            <Button
              type="primary"
              onClick={handleNext}
              className="release-advanced__submit"
            >
              Next
            </Button>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default ReleaseAdvanced;
